import { useCallback, useEffect, useRef, useState } from 'react';
import { SaveState } from '../SaveDot';

/**
 * A field's save key: which record and which column. Rows in the PEP tables
 * use the `row:` form with the table and the row id.
 */
export type FieldKey = `project:${string}` | `stage:${string}` | `row:${string}`;

/**
 * Per-field save state for the auto-saving modal. The header shows the
 * aggregate ("All changes saved"); this says *which* edit landed, so a user
 * who typed in two fields in quick succession can see both got there.
 *
 * `saved` clears itself after `clearAfterMs`. `error` stays until the field
 * is saved again, so a failure is not missed while the header moves on.
 */
export function useFieldSaveState(clearAfterMs = 1500) {
  const [states, setStates] = useState<Record<string, SaveState>>({});
  const timers = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const mark = useCallback(
    (key: FieldKey, state: SaveState) => {
      const existing = timers.current.get(key);
      if (existing) {
        clearTimeout(existing);
        timers.current.delete(key);
      }
      setStates((prev) => (prev[key] === state ? prev : { ...prev, [key]: state }));
      if (state === 'saved') {
        timers.current.set(
          key,
          setTimeout(() => {
            timers.current.delete(key);
            setStates((prev) => {
              if (prev[key] !== 'saved') return prev;
              const next = { ...prev };
              delete next[key];
              return next;
            });
          }, clearAfterMs)
        );
      }
    },
    [clearAfterMs]
  );

  const stateFor = useCallback((key: FieldKey): SaveState => states[key] ?? 'idle', [states]);

  useEffect(() => {
    const active = timers.current;
    return () => {
      active.forEach((t) => clearTimeout(t));
      active.clear();
    };
  }, []);

  return { mark, stateFor };
}
